import { Component, ChangeDetectionStrategy, Input, OnInit, Output, EventEmitter } from '@angular/core';

import { combineLatest, distinctUntilChanged, map, Observable, of, switchMap, filter } from 'rxjs';

import { finShare } from '@fcom/rx';
import { CmsPlaceholder } from '@fcom/core-api';
import { LanguageService } from '@fcom/ui-translate';
import { SortOption } from '@fcom/destination-search/interfaces';
import { LocationRouteCffService } from '@fcom/core';

import { ORIGIN_SELECTOR_CONFIG } from '../../constants';

export interface OriginSelectedEvent {
  origin: string;
  destinations?: string[];
}

@Component({
  selector: 'fin-destination-search-origin-selector',
  templateUrl: 'destination-search-origin-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DestinationSearchOriginSelectorComponent implements OnInit {
  @Input()
  content: CmsPlaceholder;

  @Input()
  selectedOriginLocationCode: string;

  @Output()
  originSelected: EventEmitter<OriginSelectedEvent> = new EventEmitter();

  countryCode$: Observable<string>;
  selected$: Observable<string>;
  selectedOriginLocationName$: Observable<string>;
  hasSelector$: Observable<boolean>;

  originOptions$: Observable<SortOption[]>;

  constructor(
    private languageService: LanguageService,
    private locationRouteCffService: LocationRouteCffService
  ) {}

  ngOnInit(): void {
    this.countryCode$ = this.languageService.countryCode;

    const customOrigins = Object.keys(this.content?.localSettings?.restrictToDestinations?.destinationsByOrigin || {});

    const origins$: Observable<string[]> =
      customOrigins.length > 0
        ? of(customOrigins)
        : this.languageService.countryCode.pipe(map((countryCode) => ORIGIN_SELECTOR_CONFIG[countryCode]));

    this.hasSelector$ = this.content?.localSettings?.disableOriginSelector
      ? of(false)
      : origins$.pipe(map((origins) => !!origins?.length));

    this.originOptions$ = origins$.pipe(
      switchMap(
        (origins) =>
          combineLatest(
            origins.map((origin) =>
              this.locationRouteCffService
                .getLocaleCityName(origin, this.languageService.localeValue)
                .pipe(map((cityName) => ({ value: origin, label: cityName })))
            )
          ) as Observable<SortOption[]>
      )
    );

    this.selected$ = this.hasSelector$.pipe(
      filter(Boolean),
      switchMap(() =>
        this.originOptions$.pipe(
          map(
            (options) =>
              options.find((option) => option.value === this.selectedOriginLocationCode)?.value || options[0].value
          )
        )
      )
    );

    this.selectedOriginLocationName$ = this.locationRouteCffService
      .bestGuessFor(this.selectedOriginLocationCode, this.languageService.localeValue)
      .pipe(
        distinctUntilChanged(),
        map((locationData) => locationData.cityName ?? this.selectedOriginLocationCode),
        finShare()
      );
  }

  selectOrigin(originLocationCode: string): void {
    this.originSelected.emit({
      origin: originLocationCode,
      destinations: this.content?.localSettings?.restrictToDestinations?.destinationsByOrigin?.[originLocationCode],
    });
  }
}
